import React, { useState } from 'react'
import {
  Box,
  Button,
  Popover,
  Grid,
  Paper,
  useTheme,
} from '@mui/material'
import { Palette as PaletteIcon } from '@mui/icons-material'

interface ColorPickerProps {
  color: string
  onChange: (color: string) => void
  disabled?: boolean
}

const ColorPicker: React.FC<ColorPickerProps> = ({
  color,
  onChange,
  disabled = false,
}) => {
  const theme = useTheme()
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)

  // Predefined color palette similar to SmartNyuki Vue app
  const colorPalette = [
    '#F8B133', // Primary orange
    '#f29100', // Accent orange
    '#ff001d', // Red
    '#049717', // Green
    '#09bdef', // Blue
    '#ffe900', // Yellow
    '#b7701f', // Brown
    '#9966ff', // Purple
    '#ff9f40', // Light orange
    '#4bc0c0', // Teal
    '#36a2eb', // Light blue
    '#ff6384', // Pink
    '#ffcd56', // Light yellow
    '#c9cbcf', // Light grey
    '#606060', // Dark grey
    '#000000', // Black
  ]

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget)
    }
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleColorSelect = (selectedColor: string) => {
    onChange(selectedColor)
    handleClose()
  }

  const open = Boolean(anchorEl)

  return (
    <>
      <Button
        variant="outlined"
        onClick={handleClick}
        disabled={disabled}
        startIcon={<PaletteIcon />}
        sx={{
          minWidth: 120,
          justifyContent: 'flex-start',
          '&::before': {
            content: '""',
            width: 20,
            height: 20,
            backgroundColor: color,
            borderRadius: 1,
            border: `1px solid ${theme.palette.divider}`,
            marginRight: 1,
          },
        }}
      >
        Color
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <Paper sx={{ p: 2, maxWidth: 240 }}>
          <Grid container spacing={1}>
            {colorPalette.map((paletteColor) => (
              <Grid item key={paletteColor}>
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    backgroundColor: paletteColor,
                    border: color === paletteColor ? `3px solid ${theme.palette.primary.main}` : `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      boxShadow: theme.shadows[2],
                    },
                  }}
                  onClick={() => handleColorSelect(paletteColor)}
                />
              </Grid>
            ))}
          </Grid>

          {/* Custom color input */}
          <Box sx={{ mt: 2 }}>
            <input
              type="color"
              value={color}
              onChange={(e) => handleColorSelect(e.target.value)}
              style={{
                width: '100%',
                height: 40,
                border: 'none',
                borderRadius: 4,
                cursor: 'pointer',
              }}
            />
          </Box>
        </Paper>
      </Popover>
    </>
  )
}

export default ColorPicker
